import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { OPPORTUNITIES } from "@/constants/testIds";
import { Sparkles, Eye, EyeOff, MapPin, Building, Bookmark, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["all", "visible", "hidden"];

export default function Opportunities() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = filter === "all" ? "" : `?kind=${filter}`;
      const { data } = await api.get(`/opportunities${params}`);
      setItems(data.items || []);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [filter]);

  const runMatch = async () => {
    setBusy(true);
    try {
      await api.post("/opportunities/match");
      await load();
      toast.success(lang === "fr" ? "Matching IA terminé" : "AI matching done");
    } catch { toast.error("AI error"); }
    finally { setBusy(false); }
  };

  const setState = async (opp_id, status) => {
    try {
      await api.post(`/opportunities/${opp_id}/state`, null, { params: { status } });
      await load();
    } catch { toast.error("Update failed"); }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div data-testid={OPPORTUNITIES.root}>
      <TopBar
        title={t("opportunities.title")}
        subtitle={t("opportunities.subtitle")}
        right={
          <button
            data-testid={OPPORTUNITIES.matchBtn}
            onClick={runMatch} disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2} />
            {busy ? t("common.loading") : t("opportunities.match")}
          </button>
        }
      />

      <div className="px-6 md:px-10 py-8">
        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-2">
          {FILTERS.map((k) => (
            <button
              key={k}
              data-testid={
                k === "all" ? OPPORTUNITIES.filterAll :
                k === "visible" ? OPPORTUNITIES.filterVisible :
                OPPORTUNITIES.filterHidden
              }
              onClick={() => setFilter(k)}
              className={`px-4 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest border transition-colors ${
                filter === k
                  ? "bg-[#0B2545] text-white border-[#0B2545]"
                  : "border-[#E4E1D8] text-[#5B5B62] hover:text-[#1F2937] hover:border-[#B8B5AC]"
              }`}
            >
              {t(`opportunities.filter_${k}`)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((opp) => (
            <OpportunityCard
              key={opp.opp_id}
              opp={opp}
              lang={lang}
              onSave={() => setState(opp.opp_id, "saved")}
              onApply={() => setState(opp.opp_id, "applied")}
              onDismiss={() => setState(opp.opp_id, "dismissed")}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function OpportunityCard({ opp, lang, onSave, onApply, onDismiss }) {
  const hidden = opp.kind === "hidden";
  const fit = opp.user_state?.fit_score;
  const status = opp.user_state?.status || "new";
  return (
    <div
      data-testid={OPPORTUNITIES.card}
      className={`relative rounded-md border p-5 transition-colors ${
        hidden
          ? "border-[#0B2545]/40 bg-gradient-to-br from-[#EBE8DE] to-[#FFFFFF] shadow-[0_0_20px_-8px_rgba(197,168,128,0.25)]"
          : "border-[#E4E1D8] bg-white hover:border-[#B8B5AC]"
      }`}
    >
      {/* Kind badge */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {fit !== undefined && fit !== null && (
          <div className="font-mono text-[10px] text-[#0B2545] border border-[#0B2545]/25 px-2 py-0.5 rounded">
            {fit}/100
          </div>
        )}
        <div className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
          hidden ? "border-[#0B2545]/40 text-[#0B2545] bg-[#0B2545]/8" : "border-[#2E8B57]/40 text-[#2E8B57] bg-[#2E8B57]/10"
        }`}>
          {hidden ? <EyeOff className="h-3 w-3" strokeWidth={1.5} /> : <Eye className="h-3 w-3" strokeWidth={1.5} />}
          {hidden ? "hidden" : "visible"}
        </div>
      </div>

      <div className="pr-32">
        <div className="font-medium text-[#1F2937] leading-snug">{opp.title}</div>
        <div className="mt-1 flex items-center gap-3 text-xs text-[#5B5B62]">
          <span className="inline-flex items-center gap-1"><Building className="h-3 w-3" strokeWidth={1.5} />{opp.company}</span>
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.5} />{opp.location}</span>
          {opp.remote && <span className="font-mono text-[10px] uppercase tracking-widest text-[#4682B4] border border-[#4682B4]/30 rounded px-1.5">remote</span>}
        </div>
      </div>

      <p className="mt-3 text-xs text-[#5B5B62] leading-relaxed line-clamp-3">{opp.description}</p>

      {hidden && opp.hidden_signal && (
        <div className="mt-3 rounded border border-[#0B2545]/20 bg-[#0B2545]/5 px-3 py-2 font-mono text-[10px] text-[#0B2545]">
          {lang === "fr" ? "SIGNAL" : "SIGNAL"}: {opp.hidden_signal}
        </div>
      )}

      {opp.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {opp.tags.slice(0, 5).map((tag) => (
            <span key={tag} className="font-mono text-[10px] text-[#5B5B62] border border-[#E4E1D8] rounded px-1.5 py-0.5">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#E4E1D8] flex items-center gap-4 flex-wrap">
        {opp.salary && <div className="font-mono text-[11px] text-[#0B2545]">{opp.salary}</div>}
        <div className="ml-auto flex items-center gap-1">
          <IconBtn testId={OPPORTUNITIES.saveBtn} active={status === "saved"} onClick={onSave} title="save">
            <Bookmark className="h-4 w-4" strokeWidth={1.5} />
          </IconBtn>
          <IconBtn testId={OPPORTUNITIES.applyBtn} active={status === "applied"} onClick={onApply} title="applied">
            <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
          </IconBtn>
          <IconBtn testId={OPPORTUNITIES.dismissBtn} active={status === "dismissed"} onClick={onDismiss} title="dismiss">
            <X className="h-4 w-4" strokeWidth={1.5} />
          </IconBtn>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ children, active, onClick, title, testId }) {
  return (
    <button
      data-testid={testId}
      title={title}
      onClick={onClick}
      className={`h-8 w-8 rounded-md border transition-colors flex items-center justify-center ${
        active
          ? "border-[#0B2545] text-[#0B2545] bg-[#0B2545]/8"
          : "border-[#E4E1D8] text-[#5B5B62] hover:text-[#1F2937] hover:border-[#B8B5AC]"
      }`}
    >
      {children}
    </button>
  );
}
